"use client";

import { c as _c } from "react/compiler-runtime";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { chatOpenAtom } from "@/lib/atoms/chat-atoms";
import { usePreferencesHydrated, usePreferencesStore } from "@/lib/stores/preferences-store";
import { CHAT_HOTKEY, ChatFab } from "./chat-fab";
interface LoadedChatClientProps {
  suggestions?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReady: () => void;
}
type ChatClientModule = {
  ChatClient: React.ComponentType<LoadedChatClientProps>;
};
type ChatClientLoader = () => Promise<ChatClientModule>;
const loadChatClient: ChatClientLoader = () => import("./chat-client");
export function ChatClientLazy(t0) {
  const $ = _c(37);
  const {
    suggestions,
    loader: t1
  } = t0;
  const loader = t1 === undefined ? loadChatClient : t1;
  const hydrated = usePreferencesHydrated();
  const hideAiFeatures = usePreferencesStore(_temp);
  const setHideAiFeatures = usePreferencesStore(_temp2);
  const [open, setOpen] = useAtom(chatOpenAtom);
  const [ChatClient, setChatClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadingRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== hideAiFeatures || $[1] !== hydrated || $[2] !== setHideAiFeatures) {
    t2 = () => {
      if (!hydrated) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("show_ai") === "1" && hideAiFeatures) {
        setHideAiFeatures(false);
        urlParams.delete("show_ai");
        const newUrl = urlParams.toString() === "" ? window.location.pathname : `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    };
    t3 = [hydrated, hideAiFeatures, setHideAiFeatures];
    $[0] = hideAiFeatures;
    $[1] = hydrated;
    $[2] = setHideAiFeatures;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== hideAiFeatures || $[6] !== setOpen) {
    t4 = () => {
      if (hideAiFeatures) {
        setOpen(false);
      }
    };
    t5 = [hideAiFeatures, setOpen];
    $[5] = hideAiFeatures;
    $[6] = setOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== ChatClient || $[10] !== loader || $[11] !== setOpen) {
    t6 = () => {
      if (ChatClient) {
        setOpen(true);
        return;
      }
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      setLoading(true);
      setLoadError(false);
      setOpen(true);
      loader().then(module => {
        setChatClient(() => module.ChatClient);
      }).catch(() => {
        loadingRef.current = false;
        setLoading(false);
        setLoadError(true);
        setOpen(false);
      });
    };
    $[9] = ChatClient;
    $[10] = loader;
    $[11] = setOpen;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleOpen = t6;
  let t7;
  if ($[13] !== ChatClient || $[14] !== handleOpen || $[15] !== setOpen) {
    t7 = () => {
      if (ChatClient) {
        setOpen(_temp3);
        return;
      }
      handleOpen();
    };
    $[13] = ChatClient;
    $[14] = handleOpen;
    $[15] = setOpen;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleActivation = t7;
  const t8 = hydrated && !hideAiFeatures;
  let t9;
  if ($[17] !== t8) {
    t9 = {
      conflictBehavior: "allow",
      enabled: t8
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  useHotkey(CHAT_HOTKEY, handleActivation, t9);
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      loadingRef.current = false;
      setLoading(false);
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleReady = t10;
  if (!hydrated) {
    return null;
  }
  let t11;
  if ($[20] !== handleActivation || $[21] !== hideAiFeatures || $[22] !== loading) {
    t11 = !hideAiFeatures && <ChatFab loading={loading} onClick={handleActivation} />;
    $[20] = handleActivation;
    $[21] = hideAiFeatures;
    $[22] = loading;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <AnimatePresence>{t11}</AnimatePresence>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== loadError) {
    t13 = loadError && <p className="sr-only" aria-live="polite">Chat failed to load. Try again.</p>;
    $[26] = loadError;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== ChatClient || $[29] !== open || $[30] !== setOpen || $[31] !== suggestions) {
    t14 = ChatClient && <ChatClient suggestions={suggestions} open={open} onOpenChange={setOpen} onReady={handleReady} />;
    $[28] = ChatClient;
    $[29] = open;
    $[30] = setOpen;
    $[31] = suggestions;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = <>{t12}{t13}{t14}</>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function _temp3(current) {
  return !current;
}
function _temp2(s_0) {
  return s_0.setHideAiFeatures;
}
function _temp(s) {
  return s.hideAiFeatures;
}